import {
  SecondaryButton,
  FormError,
  ButtonType,
} from '@huggg/react-components';
import classnames from 'classnames';
import { InfoDialog } from 'components/ui';
import dictionary from 'config/dictionary';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import * as Yup from 'yup';
import styles from './form.module.scss';

const baseValidationSchema = Yup.object().shape({
  email: Yup.string()
    .required(dictionary.forms.email.fields.email.error.required)
    .max(255)
    .email(),
});

const UIFormEmail = (props) => {
  const {
    additionalValidator,
    additionalValidatorError,
    button: ButtonComponent,
    onSubmit,
    ...rest
  } = props;

  const infoDialogRef = useRef();
  const openButtonRef = useRef();
  const additionalValidatorDialogRef = useRef();
  const openAdditionalValidatorButtonRef = useRef();
  const inputRef = useRef();
  const isFirstMount = useRef(true);

  const validationSchema = useMemo(
    () =>
      !additionalValidator
        ? baseValidationSchema
        : Yup.object().shape({
            email: Yup.reach(baseValidationSchema, 'email').concat(
              Yup.string().test(
                'additionalValidator',
                'Additional validator error',
                additionalValidator,
              ),
            ),
          }),
    [additionalValidator],
  );

  const initialValues = {
    email:
      (process.env.NODE_ENV === 'development' && process.env.REACT_APP_EMAIL) ||
      '',
  };

  const onEmailChange = useCallback((event, { setFieldValue }) => {
    const value = event.target.value;
    const formatted = value.replace(/^\s+/g, '');
    setFieldValue('email', formatted);
  }, []);

  useEffect(() => {
    isFirstMount.current = false;
  }, []);

  const isAdditionalValidatorError = (message) =>
    message === 'Additional validator error';

  const getErrorMessageText = (message) => {
    if (
      isAdditionalValidatorError(message) &&
      additionalValidatorError?.message
    ) {
      return additionalValidatorError.message;
    }

    return dictionary.forms.email.fields.email.error.invalid.text;
  };

  const getErrorButton = (message) => {
    const avError = isAdditionalValidatorError(message);

    if (avError && !additionalValidatorError?.buttonText) {
      return <></>;
    }

    return (
      <button
        type="button"
        onClick={
          avError
            ? additionalValidatorDialogRef.current.open
            : infoDialogRef.current.open
        }
        className={styles.errorMessageButton}
        ref={avError ? openAdditionalValidatorButtonRef : openButtonRef}
      >
        {avError
          ? additionalValidatorError.buttonText
          : dictionary.forms.email.fields.email.error.invalid.button}
      </button>
    );
  };

  const renderErrorMessage = (className, message) => (
    <div className={styles.errorContainer}>
      <FormError className={className}>
        {/required|try again/.test(message) ? (
          message
        ) : (
          <span>
            {getErrorMessageText(message)} {getErrorButton(message)}
          </span>
        )}
      </FormError>
    </div>
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
      >
        {(formikProps) => {
          const { errors, setFieldValue, touched, values } = formikProps;

          return (
            <Form {...rest}>
              <label htmlFor="email" className={styles.label}>
                {dictionary.forms.email.fields.email.label}
              </label>
              <Field
                type="email"
                id="email"
                name="email"
                maxLength="255"
                data-test-id="stash-submit-input"
                innerRef={inputRef}
                className={classnames(styles.input, styles.inputTextCenter, {
                  [styles.inputError]: errors.email && touched.email,
                })}
                value={values.email}
                onChange={(event) =>
                  onEmailChange(event, {
                    setFieldValue,
                  })
                }
              />

              {errors.email &&
                renderErrorMessage(styles.errorMessage, errors['email'])}

              {React.cloneElement(ButtonComponent, {
                type: ButtonType.Submit,
                disabled:
                  Object.keys(errors).length > 0 || formikProps.isSubmitting,
              })}
            </Form>
          );
        }}
      </Formik>

      <InfoDialog
        ref={infoDialogRef}
        openButtonRef={openButtonRef}
        body={
          <>
            <div className={styles.validationRulesMessage}>
              {dictionary.forms.email.dialogs.email.title}
            </div>

            <ul className={styles.validationRules}>
              {dictionary.forms.email.dialogs.email.body.map((point, index) => (
                <li className={styles.validationRule} key={index}>
                  {point}
                </li>
              ))}
            </ul>
          </>
        }
      />

      <InfoDialog
        ref={additionalValidatorDialogRef}
        openButtonRef={openAdditionalValidatorButtonRef}
        body={
          <>
            <div className={styles.validationRulesMessage}>
              {additionalValidatorError?.dialog?.title ||
                dictionary.forms.email.dialogs.email.title}
            </div>

            <ul className={styles.validationRules}>
              {(
                additionalValidatorError?.dialog?.body ||
                dictionary.forms.email.dialogs.email.body
              ).map((point, index) => (
                <li className={styles.validationRule} key={index}>
                  {point}
                </li>
              ))}
            </ul>
          </>
        }
      />
    </>
  );
};

UIFormEmail.defaultProps = {
  button: <SecondaryButton />,
};

UIFormEmail.propTypes = {
  additionalValidator: PropTypes.func,
  additionalValidatorError: PropTypes.shape({
    message: PropTypes.string,
    buttonText: PropTypes.string,
    dialog: PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  onSubmit: PropTypes.func.isRequired,
  button: PropTypes.element,
};

export default UIFormEmail;
